// Tooltip — info tooltip (tooltips.css: .info-tooltip + .tooltip-text). The firms page keeps the
// CSS hover popover. `portal` uses that same popover, painted on document.body so a scrolling
// table cannot clip it.
import { useState, useRef, type ReactNode, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';

export interface TooltipProps {
  content: ReactNode;
  placement?: 'top' | 'bottom';
  children?: ReactNode;
  portal?: boolean;
}

export default function Tooltip({
  content,
  placement = 'bottom',
  children,
  portal = false,
}: TooltipProps) {
  const trigger = children ?? <i className="bi bi-info-circle info-icon" aria-hidden="true" />;
  const ref = useRef<HTMLSpanElement>(null);
  const [box, setBox] = useState<{ top: number; left: number } | null>(null);

  if (!portal) {
    return (
      <span className="info-tooltip" data-placement={placement}>
        {trigger}
        <span className="tooltip-text" role="tooltip">
          {content}
        </span>
      </span>
    );
  }

  const place = () => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    setBox({
      top: placement === 'top' ? rect.top - 8 : rect.bottom + 8,
      left: rect.left + rect.width / 2,
    });
  };

  const style: CSSProperties | undefined = box
    ? {
        top: box.top,
        left: box.left,
        transform: placement === 'top' ? 'translate(-50%, -100%)' : 'translate(-50%, 0)',
      }
    : undefined;

  return (
    <span
      ref={ref}
      className="info-tooltip"
      data-placement={placement}
      onMouseEnter={place}
      onMouseOver={place}
      onFocus={place}
      onMouseLeave={() => setBox(null)}
      onBlur={() => setBox(null)}
    >
      {trigger}
      {box &&
        createPortal(
          <span className="tooltip-text tooltip-text--portal" role="tooltip" style={style}>
            {content}
          </span>,
          document.body,
        )}
    </span>
  );
}
